import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';

import saveFile from 'helpers/saveFile';
import { FilterMatchMode } from 'primereact/api';
import { addLocale, locale } from 'primereact/api';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import {
  DataTable,
  DataTableFilterMetaData,
  DataTablePageEvent,
  DataTableSortMeta,
  DataTableStateEvent
} from 'primereact/datatable';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Toast } from 'primereact/toast';

import 'primeicons/primeicons.css';
import 'primeflex/primeflex.css';

import SearchInputWithClear from 'ui/custom-controls/SearchInputWithClear';
import OperationTypeCard from 'ui/OperationTypeCard';
import DeleteConfirmationDialog from 'ui/shared/DeleteConfirmationDialog';
import { ImportErrorDialog } from 'ui/shared/ImportErrorDialog';
import TablePageHeader from 'ui/shared/TablePageHeader';

import { OperationTypesTemplate } from '../constants/OperationTypesTemplate';
import { isSelected } from '../helpers';
import { useDebounce } from '../hooks';
import useOperationTypesService from '../hooks/services/useOperationTypesService';
import { ruLocale } from '../lang/ruLocale';
import { OperationType } from '../types/interfaces';
import pageSizeValues from '../ui/shared/paginator/pageSizeValues';
import { clearFilter, generateTags } from '../utils';
import VisibilityWrapper from '../utils/wrappers/VisibilityWrapper';

export default function OperationTypes() {
  const [operationTypes, setOperationTypes] = useState<OperationType[]>([]);
  const [dialogVisible, setDialogVisible] = useState<boolean>(false);
  const [validationErrors, setValidationErrors] = useState<{ error: string }[]>(
    []
  );
  const [errorDialogVisible, setErrorDialogVisible] = useState<boolean>(false);
  const [deleteDialogVisible, setDeleteDialogVisible] =
    useState<boolean>(false);
  const [OperationTypeToDelete, setOperationTypeToDelete] = useState<
    string | number | null
  >(null);
  const [selectedOperationType, setSelectedOperationType] =
    useState<OperationType>(OperationTypesTemplate);
  const [filters, setFilters] = useState<
    Record<string, DataTableFilterMetaData>
  >({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    operationName: { value: null, matchMode: FilterMatchMode.CONTAINS },
    measureName: { value: null, matchMode: FilterMatchMode.CONTAINS },
    attainments : { value: null, matchMode: FilterMatchMode.CONTAINS },
    effort : { value: null, matchMode: FilterMatchMode.CONTAINS },
    value: { value: null, matchMode: FilterMatchMode.CONTAINS }
  });

  const [loading, setLoading] = useState<boolean>(true);
  const operationNameFilterRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const unitMeasurementFilterRef = useRef<HTMLInputElement>(null);
  const attainmentsFilterRef = useRef<HTMLInputElement>(null);
  const effortFilterRef = useRef<HTMLInputElement>(null);

  const globalFilterRef = useRef<HTMLInputElement>(null);
  const toast = useRef<Toast>(null);
  const operationTypeCardRef = useRef<null | {
    handleCancel: () => void;
  }>(null);

  const isSelectedIdExist = isSelected(selectedOperationType);
  const [pageSize, setPageSize] = useState(
    pageSizeValues[pageSizeValues.length - 1].value
  );
  const [pageNumber, setPageNumber] = useState(1);
  const [totalCount, setTotalCount] = useState(1);
  const [first, setFirst] = useState(0);
  const [multiSortMeta, setMultiSortMeta] = useState<
    DataTableSortMeta[] | undefined | null
  >(undefined);
  const setMultiSortMetaCallback = useCallback((e: DataTableStateEvent) => {
    setLoading(true);
    setMultiSortMeta(e.multiSortMeta);
  }, []);
  const onPageChanged = useCallback(async (event: DataTablePageEvent) => {
    if (event.rows !== undefined) {
      setFirst(0);
      setPageSize(event.rows);
    }

    if (event.page !== undefined) {
      setFirst(event.first);
      setPageNumber(event.first / event.rows + 1);
    }
  }, []);
 
  addLocale('ru', ruLocale.ru);
  locale('ru');
  const operationTypesService = useOperationTypesService();
  const getOperationTypesCallback = useCallback(() => {
    const sort = multiSortMeta?.find((x) => x !== undefined);
    const operationNameFilter =
      filters.operationName as DataTableFilterMetaData;
    const measureFilter = filters.measureName as DataTableFilterMetaData;
    const globalFilter = filters.global as DataTableFilterMetaData;
    const attainmentsFilter = filters.attainments as DataTableFilterMetaData;
    const effortFilter = filters.effort as DataTableFilterMetaData;

    const serviceResult = operationTypesService.getAll({
      pageNumber,
      pageSize,
      sortBy: sort?.field,
      sortDirection: sort?.order === 1 ? 'asc' : 'desc',
      filters: JSON.stringify([
        {
          field: 'operationName',
          value: operationNameFilter.value,
          operator: operationNameFilter.matchMode
        },
        {
          field: 'measure',
          value: measureFilter.value,
          operator: measureFilter.matchMode
        },
        {
          field: 'global',
          value: globalFilter.value,
          operator: globalFilter.matchMode
        },
        {
          field: 'attainments',
          value: attainmentsFilter.value,
          operator: attainmentsFilter.matchMode
        },
        {
          field: 'effort',
          value: effortFilter.value,
          operator: effortFilter.matchMode
        }
      ])
    });

    serviceResult.result.then((x) => {
      if (x) {
        setTotalCount((x.total === 0 ? 1 : x.total) ?? 1);
        setOperationTypes(
          x.itemsList.map((x) => ({
            ...x,
            measureName: x.measure?.label ?? ''
          }))
        );
      }
      setLoading(false);
    });

    return () => serviceResult.cancel();
  }, [filters, multiSortMeta, operationTypesService, pageNumber, pageSize]);

  useEffect(() => {
    const cancel = getOperationTypesCallback();
    return () => cancel();
  }, [getOperationTypesCallback]);

  const onFilterChange = (
    filter: string,
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = e.target.value;
    const _filters = JSON.parse(JSON.stringify(filters));
    _filters[filter].value = value;
    setPageNumber(1);
    setFirst(0);
    setFilters(_filters);
  };

  const onFiltersChange = useCallback((e: DataTableStateEvent) => {
    setPageNumber(1);
    setFirst(0);

    const next = e.filters as Record<string, DataTableFilterMetaData>;
    setFilters(next);
  }, []);

  const { debouncedCallback: debouncedOnFilterChange } = useDebounce(
    onFilterChange,
    300
  );

  const handleInputChange =
    (filter: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
      debouncedOnFilterChange(filter, e);
    };

  const addButtonHandle = () => {
    setSelectedOperationType(OperationTypesTemplate);
    setDialogVisible(true);
  };

  // Export Template logic
  const exportTemplate = useCallback(async () => {
    const result = await operationTypesService.getExcelTemplate();
    await saveFile(result.data, 'Работы шаблон.xls', '');
  }, []);

  const importAssetsXLS = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);

    try {
      // Validate the file
      const validationResponse = await operationTypesService.validate(formData);

      if (validationResponse?.data?.errors?.length > 0) {
        setValidationErrors(
          validationResponse.data.errors.map((i) => ({ error: i }))
        );
        setErrorDialogVisible(true);
        return;
      }

      // Import the file (PUT)
      const importResponse = await operationTypesService.put(formData);
      const getImportMessage = (count: number) => {
        if (count === 1 || (count % 10 === 1 && count % 100 !== 11)) {
          return `${count} работа импортирована.`;
        } else if (
          (count >= 2 && count <= 4) ||
          (count % 10 >= 2 &&
            count % 10 <= 4 &&
            (count % 100 < 12 || count % 100 > 14))
        ) {
          return `${count} работы импортировано.`;
        } else {
          return `${count} работ импортировано.`;
        }
      };

      toast.current?.show({
        severity: 'success',
        summary: 'Импорт успешно завершен',
        detail: getImportMessage(importResponse.data.operationTypes.length),
        sticky: true
      });

      // Optionally reload data or trigger a refresh
      await getOperationTypesCallback();
    } catch (error) {
      console.error('Ошибка импорта:', error);
      toast.current?.show({
        severity: 'error',
        summary: 'Ошибка импорта',
        detail: 'Произошла ошибка при импорте файла.',
        sticky: true
      });
    }
  };


  const handleDeleteConfirmation = () => {
    handleDelete(OperationTypeToDelete);
    setDeleteDialogVisible(false);
  };

  const handleUpdate = async (
    id: string | number,
    updatedOperation: OperationType
  ) => {
    const result =
      await operationTypesService.addOrUpdate(updatedOperation).result;
    getOperationTypesCallback();
    return result;
  };

  const handleDelete = async (id: string | number | null) => {
    if (id) {
      await operationTypesService.delete(parseInt(id.toString())).result;
      getOperationTypesCallback();
    }
  };

  const handleAdd = async (operationTypes: OperationType) => {
    const result =
      await operationTypesService.addOrUpdate(operationTypes).result;
    getOperationTypesCallback();
    return result;
  };

  const closeDialog = () => {
    // Function to close the dialog in parent
    setDialogVisible(false);
  };

  const onHideOperationTypeCard = () => {
    if (operationTypeCardRef.current !== null) {
      operationTypeCardRef.current.handleCancel();
    }
  };
  const editButtonHandle = () => {
    if (toast.current && !isSelectedIdExist) {
      toast.current.show({
        severity: 'warn',
        summary: 'Ошибка',
        detail: 'Выберите работу для редактирования',
        life: 3000
      });
    }
    if (isSelectedIdExist) setDialogVisible(true);
  }

  const deleteButtonHandle = () => {
    // If a valid process is selected, render the deletion confirmation dialog.
    if (isSelectedIdExist) {
      setOperationTypeToDelete(selectedOperationType.id);
      setDeleteDialogVisible(true);
    }
    if (toast.current && !isSelectedIdExist) {
      toast.current.show({
        severity: 'warn',
        summary: 'Ошибка',
        detail: 'Выберите работу для удаления',
        life: 3000
      });
    }
  };

  const header = (  
    <TablePageHeader
      tableName="operationTypes"
      onGlobalSearchChange={value =>
        handleInputChange('global')({ target: { value } } as React.ChangeEvent<HTMLInputElement>)
      }
      onAdd={addButtonHandle}
      onEdit={editButtonHandle}
      onDelete={deleteButtonHandle}
      customButtonsRight={[
        <input
          type="file"
          key="input"
          accept="csv"
          onChange={importAssetsXLS}
          style={{ visibility: 'hidden' }}
          ref={fileInputRef}
        />,
        <Button
          className="operationTypes-import-btn__roles"
          key="export-template-button"
          type="button"
          severity="secondary"
          icon="pi pi-file-excel"
          rounded
          onClick={exportTemplate}
          tooltip="Выгрузка актуального шаблона импорта"
        />,
        <Button
          className="operationTypes-import-btn__roles"
          key="import-button"
          type="button"
          severity="secondary"
          icon="pi pi-file-import"
          rounded
          onClick={() => {
            if (fileInputRef.current) {
              fileInputRef.current.value = '';
            }
            fileInputRef.current?.click();
          }}
          label="Импорт"
        />
      ]}
    />
  )

  const tableElement = document.querySelector('.p-datatable-thead');

  function offset(el: Element) {
    var rect = el.getBoundingClientRect(),
      scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    return { top: rect.top + scrollTop };
  }

  const scrollHeight = useMemo(() => {
    const paginatorElement = document.querySelector('.p-paginator');
    const paginatorHeight =
      paginatorElement instanceof HTMLElement
        ? paginatorElement.offsetHeight
        : 0;
    const tableOffset =
      tableElement instanceof HTMLElement ? offset(tableElement).top : 0;
    return `calc(100vh - ${
      tableElement instanceof HTMLElement ? tableOffset + paginatorHeight : 0
    }px)`;
  }, [tableElement]);

  return (
    <div className="operationTypes-table wrapper wrapper--main">
      <Toast ref={toast} />

      <ImportErrorDialog
        errorDialogVisible={errorDialogVisible}
        setErrorDialogVisible={setErrorDialogVisible}
        validationErrors={validationErrors}
      />

      <Dialog
        header="Работа"
        visible={dialogVisible}
        style={{ minWidth: '600px', width: '600px', minHeight: 'fit-content' }}
        maximizable
        modal
        onHide={() => onHideOperationTypeCard()}
      >
        <VisibilityWrapper code="operationTypes-card__roles">
          <OperationTypeCard
            operationTypeProps={selectedOperationType}
            onUpdate={handleUpdate}
            onDelete={handleDelete}
            onAdd={handleAdd}
            closeDialog={closeDialog}
            ref={operationTypeCardRef}
          />
        </VisibilityWrapper>
      </Dialog>

      <DeleteConfirmationDialog
        visible={deleteDialogVisible}
        onHide={() => setDeleteDialogVisible(false)}
        onConfirm={handleDeleteConfirmation}
        entityType="работу"
        entityName={selectedOperationType?.operationName}
      />
      <DataTable
        filters={filters}
        paginator
        first={first}
        rows={pageSize}
        onPage={onPageChanged}
        totalRecords={totalCount}
        rowsPerPageOptions={[10, 25, 50, 100]}
        paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink JumpToPageInput"
        currentPageReportTemplate="{currentPage} из {totalPages}"
        paginatorLeft={' '}
        paginatorRight={`Всего: ${operationTypes.length > 0 ? totalCount : 0}`}
        lazy
        multiSortMeta={multiSortMeta}
        onSort={setMultiSortMetaCallback}
        selectionMode="single"
        selection={selectedOperationType!}
        onSelectionChange={(e) => setSelectedOperationType(e.value)}
        onRowDoubleClick={(e) => {
          setDialogVisible(true);
          setSelectedOperationType(e.data as OperationType);
        }}
        metaKeySelection={false}
        scrollable
        sortMode="multiple"
        removableSort
        scrollHeight={scrollHeight}
        size="small"
        resizableColumns
        reorderableColumns
        reorderableRows
        value={operationTypes}
        dataKey="id"
        filterDisplay="row"
        loading={loading}
        header={header}
        emptyMessage="Работы не найдены."
        onRowReorder={(e) => setOperationTypes(e.value)}
        tableStyle={{ minWidth: '50rem' }}
        onFilter={onFiltersChange}
      >
        <Column
          columnKey="operationName"
          sortable
          field="operationName"
          header="Название работы"
          filter
          onFilterClear={() =>
            clearFilter(
              'operationName',
              filters,
              setFilters,
              operationNameFilterRef,
              setPageNumber,
              setFirst
            )
          }
          filterElement={
            <div className="flex justify-content-end">
              <InputText
                ref={operationNameFilterRef}
                onChange={handleInputChange('operationName')}
                placeholder="Поиск по названию работ"
              />
            </div>
          }
          style={{ width: '15%' }}
        />
        <Column
          columnKey="measureName"
          sortable
          field="measureName"
          header="Единица измерения"
          sortField="measure"
          filter
          onFilterClear={() =>
            clearFilter(
              'measureName',
              filters,
              setFilters,
              unitMeasurementFilterRef,
              setPageNumber,
              setFirst
            )
          }
          filterElement={
            <div className="flex justify-content-end">
              <InputText
                ref={unitMeasurementFilterRef}
                onChange={handleInputChange('measureName')}
                placeholder="Поиск по единицам измерения"
              />
            </div>
          }
          style={{ width: '17%' }}
        />
        <Column
          columnKey="attainments"
          field="attainments"
          header="Навыки и допуски"
          body={(taskTypeData: OperationType | undefined) =>
            generateTags(taskTypeData, 'attainments')
          }
          filter
          onFilterClear={() =>
            clearFilter(
              'attainments',
              filters,
              setFilters,
              attainmentsFilterRef
            )
          }
          filterElement={
            <div className="flex justify-content-end">
              <InputText
                ref={attainmentsFilterRef}
                onChange={handleInputChange('attainments')}
                placeholder="Поиск по навыкам и допускам"
              />
            </div>
          }
          style={{ width: '58%' }}
        />
        <Column
          columnKey="effort"
          field="effort"
          header="Трудозатраты (мин)"
          filter
          sortable  
          sortField="effort"
          onFilterClear={() =>
            clearFilter(
              'effort',
              filters,
              setFilters,
              effortFilterRef
            )
          }
          filterElement={
            <div className="flex justify-content-end">
              <InputText
                ref={effortFilterRef}
                onChange={handleInputChange('effort')}
                placeholder="Поиск по трудозатратам"
              />
            </div>
          }
          style={{ width: '10%' }}
        />

        
      </DataTable>
    </div>
  );
}
